/**
 * Push Subscription Service
 * AIDLC Administrative Backend
 *
 * Subscribes this browser to push notifications (PUSH-SUB-1, BL-7eaa1b29), asking the browser's permission only when
 * the person clicks "Turn on notifications on this browser" in Settings (PUSH-ASK-1, BL-e113d148).
 *
 * - On load, subscribeToPush re-syncs only when notifications are already allowed: a page load never asks.
 * - turnOnBrowserPush asks, from the click, and subscribes once allowed.
 * - readBrowserPushState says where this browser stands, for the Settings status line.
 *
 * Push is optional: when the server has no VAPID keys (503), the browser has no push support, or the user
 * has blocked notifications, nothing is subscribed; the reason goes to the debug log.
 * A real fault (the subscribe or register call failing) is a console warning; the portal keeps working.
 */

import apiClient from './api';
import { isPushNotConfigured, vapidKeyToBytes, vapidPublicKeyFrom } from '../utils/webPushKey';
import { browserPushState, shouldResyncOnLoad, type BrowserPushState } from '../utils/browserPush';

const LOG_PREFIX = '[Push]';

/** Why no subscription was made, or that one was. Returned for tests and the debug log. */
export type PushSubscribeOutcome =
  | 'unsupported'
  | 'blocked'
  | 'not-asked'
  | 'already-subscribed'
  | 'not-configured'
  | 'declined'
  | 'subscribed'
  | 'failed';

/** The server's VAPID public key, or null when push is not set up there (a 503, said only in the debug log). */
async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const { data } = await apiClient.get<{ publicKey?: string }>('/notifications/vapid-key');
    return vapidPublicKeyFrom(data);
  } catch (error) {
    if (isPushNotConfigured(error)) return null;
    throw error;
  }
}

/** A real fault (not a quiet 'no'): one console warning, and the portal keeps working. */
function warnFault(message: string, error?: unknown): void {
  console.warn(`${LOG_PREFIX} ${message}`, ...(error === undefined ? [] : [error]));
}

function isPermissionDenied(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === 'NotAllowedError';
}

function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Subscribes this browser and registers the subscription with the server, when notifications are already allowed;
 * never asks and never throws. App.tsx calls it on load to re-sync.
 */
export async function subscribeToPush(): Promise<PushSubscribeOutcome> {
  if (!pushSupported()) {
    console.debug(`${LOG_PREFIX} This browser has no push support; not subscribing.`);
    return 'unsupported';
  }
  if (Notification.permission === 'denied') {
    console.debug(`${LOG_PREFIX} Notifications are blocked for this site; not subscribing.`);
    return 'blocked';
  }
  if (!shouldResyncOnLoad(Notification.permission)) {
    console.debug(`${LOG_PREFIX} Notifications have not been allowed yet; not asking on load.`);
    return 'not-asked';
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    if (await registration.pushManager.getSubscription()) return 'already-subscribed';

    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) {
      console.debug(`${LOG_PREFIX} Push is not set up on the server; not subscribing.`);
      return 'not-configured';
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyToBytes(publicKey),
    });
    await apiClient.post('/notifications/subscribe', { subscription: subscription.toJSON() });
    console.info(`${LOG_PREFIX} Subscription registered`);
    return 'subscribed';
  } catch (error) {
    if (isPermissionDenied(error)) {
      console.debug(`${LOG_PREFIX} Notifications were not allowed; not subscribing.`);
      return 'declined';
    }
    warnFault('Subscription failed:', error);
    return 'failed';
  }
}

/**
 * From the Settings button only: asks the browser's permission, then subscribes once allowed. Never throws.
 * With no service worker registered there is nothing to subscribe with (and `ready` would never settle), so it
 * says 'failed' before asking anything.
 */
export async function turnOnBrowserPush(): Promise<PushSubscribeOutcome> {
  if (!pushSupported()) return 'unsupported';
  try {
    if (!(await navigator.serviceWorker.getRegistration())) {
      warnFault('No service worker is registered; cannot turn notifications on.');
      return 'failed';
    }
  } catch (error) {
    warnFault('Reading the service worker registration failed:', error);
    return 'failed';
  }
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch (error) {
    console.debug(`${LOG_PREFIX} The permission request did not complete:`, error);
    return 'declined';
  }
  if (permission === 'denied') return 'blocked';
  if (permission !== 'granted') return 'declined';
  return subscribeToPush();
}

/** Where this browser stands, for the Settings status line. Never asks. Throws when the server cannot be read. */
export async function readBrowserPushState(): Promise<BrowserPushState> {
  if (!pushSupported()) return browserPushState({ supported: false, permission: null, configured: false, subscribed: false });
  const permission = Notification.permission;
  const configured = (await fetchVapidPublicKey()) !== null;
  // getRegistration, not ready: ready never settles when the service worker failed to register.
  const registration = await navigator.serviceWorker.getRegistration();
  const subscribed = Boolean(registration && (await registration.pushManager.getSubscription()));
  return browserPushState({ supported: true, permission, configured, subscribed });
}
